/**
 * @file InfiniteScroll.component.tsx
 * @module app/components/InfiniteScroll.component
 * @description Infinite scroll wrapper component.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.1.1
 */

import React, { useEffect, useRef } from "react";
import Loader from "../../Global/loader";

interface InfiniteScrollProps {
  children: React.ReactNode;
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
  threshold?: number;
  className?: string;
}

/**
 * Wraps a list/table and fetches the next batch once a bottom sentinel enters the viewport.
 */
export const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  children,
  hasMore,
  loading,
  onLoadMore,
  rootMargin = "0px 0px 200px 0px",
  threshold = 0,
  className,
}) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Keep the latest callback without re-creating the observer on every render
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      { root: null, rootMargin, threshold },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, rootMargin, threshold]);

  return (
    <div className={className}>
      {children}
      {hasMore && (
        <div ref={sentinelRef} className="d-flex justify-content-center py-4">
          {loading && <Loader loading={true} />}
        </div>
      )}
    </div>
  );
};

export default InfiniteScroll;
